import React, {useCallback, useLayoutEffect, useState} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {DataGridPremium, useGridApiRef} from '@mui/x-data-grid-premium';

// Strip state subtrees that should never be persisted across mounts.
// `columns.orderedFields` is excluded by default — restoring a previously-saved
// column order onto a grid whose columns prop has changed (different category,
// added/removed fields) leaves the displayed order mismatched. Pass
// `persistColumnOrder` to opt back into the old behavior.
const sanitizeState = (raw, {persistColumnOrder}) => {
    if (!raw) return raw;
    if (persistColumnOrder) return raw;
    const next = {...raw, columns: {...(raw.columns || {})}};
    if (next.columns && next.columns.orderedFields) {
        delete next.columns.orderedFields;
    }
    return next;
};

const StatefulDataGrid = props => {
    const {apiRef: rawApiRef, stateId, rows, persistColumnOrder, ...rest} = props;
    if (!stateId) {
        throw new Error('StatefulDataGrid requires a stateId prop');
    }

    const [initialState, setInitialState] = useState();
    // Call the hook unconditionally (rules-of-hooks); fall back to it only when
    // the caller didn't supply an apiRef.
    const fallbackApiRef = useGridApiRef();
    const apiRef = rawApiRef || fallbackApiRef;
    const stateKey = `statefulDataGrid-${stateId}`;

    const saveSnapshot = useCallback(() => {
        if (apiRef?.current?.exportState && localStorage) {
            const currentState = apiRef.current.exportState();
            const cleaned = sanitizeState(currentState, {persistColumnOrder});
            localStorage.setItem(stateKey, JSON.stringify(cleaned));
        }
    }, [apiRef, stateKey, persistColumnOrder]);

    useLayoutEffect(() => {
        const stateFromLocalStorage = localStorage?.getItem(stateKey);
        const parsed = stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {};
        setInitialState(sanitizeState(parsed, {persistColumnOrder}) || {});

        window.addEventListener('beforeunload', saveSnapshot);

        return () => {
            window.removeEventListener('beforeunload', saveSnapshot);
            saveSnapshot();
        };
    }, [saveSnapshot, stateKey, persistColumnOrder]);

    if (!initialState) {
        return <CircularProgress />;
    }

    // Deep merge `columns` and `columns.columnVisibilityModel` so prop-level
    // defaults (e.g. {createdAt: false}) aren't clobbered by a shallow spread
    // of an unrelated localStorage entry. User overrides for the same key
    // still win because the localStorage layer is spread last.
    const propInitial = rest.initialState || {};
    const propColumns = propInitial.columns || {};
    const lsColumns = initialState.columns || {};
    const mergedInitialState = {
        ...propInitial,
        ...initialState,
        columns: {
            ...propColumns,
            ...lsColumns,
            columnVisibilityModel: {
                ...(propColumns.columnVisibilityModel || {}),
                ...(lsColumns.columnVisibilityModel || {})
            }
        }
    };

    return (
        <DataGridPremium
            apiRef={apiRef}
            rows={rows}
            {...rest}
            initialState={mergedInitialState}
        />
    );
};

export default StatefulDataGrid;
